import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { MobileProvider } from './contexts/MobileContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { PageShell } from './components/PageShell'
import ErrorBoundary from './components/ErrorBoundary'

// Pages
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import SearchPage from './pages/SearchPage'
import IntakePage from './pages/IntakePage'
import AdminPage from './pages/AdminPage'
import ItemDetailPage from './pages/ItemDetailPage'
import QRScannerPage from './pages/QRScannerPage'
import ReturnsPage from './pages/ReturnsPage'
import AnalyticsPage from './pages/AnalyticsPage'
import ChecklistsPage from './pages/ChecklistsPage'
import QRGeneratorPage from './pages/QRGeneratorPage'
import MobilePage from './pages/MobilePage'
import EnterSalePage from './pages/EnterSalePage'
import PickListsPage from './pages/PickListsPage'
import EscapeRoutePage from './pages/escaperoute/EscapeRoutePage'

// Admin subpages
import CategoriesPage from './pages/admin/CategoriesPage'
import CustomFieldsPage from './pages/admin/CustomFieldsPage'
import LocationsPage from './pages/admin/LocationsPage'
import AIPromptsPage from './pages/admin/AIPromptsPage'
import TotesPage from './pages/admin/TotesPage'
import ShelvesPage from './pages/admin/ShelvesPage'
import UsersPage from './pages/admin/UsersPage'
import SystemPicksPage from './pages/admin/SystemPicksPage'
import TemplateMappingPage from './pages/admin/TemplateMappingPage'
import SystemHealthPage from './pages/admin/SystemHealthPage'
import LabelDesignerPage from './pages/admin/LabelDesignerPage'
import AdminReturnsPage from './pages/admin/ReturnsPage'
import AIMonitoringPage from './pages/admin/AIMonitoringPage'
import TiersPage from './pages/admin/TiersPage'
import TierDetailPage from './pages/TierDetailPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
})

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <MobileProvider>
              <NotificationProvider>
                <Router>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />

                  <Route path="/search" element={
                    <PageShell config={{
                      id: 'search',
                      title: 'Search Items',
                      description: 'Find and manage inventory items',
                      actions: [],
                      layout: { containerWidth: 'xl', columns: 1 }
                    }}>
                      <SearchPage />
                    </PageShell>
                  } />

                  <Route path="/intake" element={
                    <PageShell config={{
                      id: 'intake',
                      title: 'Inventory Intake',
                      description: 'Add new items to your inventory',
                      actions: [],
                      layout: { containerWidth: 'lg', columns: 1 }
                    }}>
                      <IntakePage />
                    </PageShell>
                  } />

                  <Route path="/admin" element={
                    <PageShell config={{
                      id: 'admin',
                      title: 'Administration',
                      description: 'System administration and settings',
                      actions: [],
                      layout: { containerWidth: 'xl', columns: 1 }
                    }}>
                      <AdminPage />
                    </PageShell>
                  } />

                  <Route path="/items/:id" element={
                    <PageShell config={{
                      id: 'item-detail',
                      title: 'Item Details',
                      description: 'View and edit item information',
                      actions: [],
                      layout: { containerWidth: 'lg', columns: 1 }
                    }}>
                      <ItemDetailPage />
                    </PageShell>
                  } />

                  <Route path="/qr-scanner" element={
                    <PageShell config={{
                      id: 'qr-scanner',
                      title: 'QR/Barcode Scanner',
                      description: 'Scan QR codes and barcodes',
                      actions: [],
                      layout: { containerWidth: 'lg', columns: 1 }
                    }}>
                      <QRScannerPage />
                    </PageShell>
                  } />

                  <Route path="/returns" element={
                    <PageShell config={{ id: 'returns', title: 'Returns Management', description: 'Process returns and exchanges', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <ReturnsPage />
                    </PageShell>
                  } />

                  <Route path="/analytics" element={
                    <PageShell config={{ id: 'analytics', title: 'Analytics Dashboard', description: 'View reports and analytics', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <AnalyticsPage />
                    </PageShell>
                  } />

                  <Route path="/checklists" element={
                    <PageShell config={{ id: 'checklists', title: 'Task Management', description: 'Create and manage checklists', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <ChecklistsPage />
                    </PageShell>
                  } />

                  <Route path="/qr-generator" element={
                    <PageShell config={{ id: 'qr-generator', title: 'QR Code Generator', description: 'Generate QR codes', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <QRGeneratorPage />
                    </PageShell>
                  } />

                  <Route path="/mobile" element={
                    <PageShell config={{ id: 'mobile', title: 'Mobile Dashboard', description: 'Mobile-optimized interface', actions: [], layout: { containerWidth: '2xl', columns: 1 } }}>
                      <MobilePage />
                    </PageShell>
                  } />

                  <Route path="/enter-sale" element={
                    <PageShell config={{ id: 'enter-sale', title: 'Enter Sale Details', description: 'Record sale information', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <EnterSalePage />
                    </PageShell>
                  } />

                  <Route path="/picklists" element={
                    <PageShell config={{ id: 'picklists', title: 'Pick Lists', description: 'Manage and process pick lists', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <PickListsPage />
                    </PageShell>
                  } />

                  {/* Admin subpages */}
                  <Route path="/admin/categories" element={
                    <PageShell config={{ id: 'admin-categories', title: 'Categories Management', description: 'Manage product categories', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <CategoriesPage />
                    </PageShell>
                  } />

                  <Route path="/admin/custom-fields" element={
                    <PageShell config={{ id: 'admin-custom-fields', title: 'Custom Fields Management', description: 'Configure custom fields', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <CustomFieldsPage />
                    </PageShell>
                  } />

                  <Route path="/admin/locations" element={
                    <PageShell config={{ id: 'admin-locations', title: 'Locations Management', description: 'Manage storage locations', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <LocationsPage />
                    </PageShell>
                  } />

                  <Route path="/admin/ai-prompts" element={
                    <PageShell config={{ id: 'admin-ai-prompts', title: 'Universal AI Prompt', description: 'Manage AI prompts', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <AIPromptsPage />
                    </PageShell>
                  } />

                  <Route path="/admin/totes" element={<TotesPage />} />
                  <Route path="/admin/shelves" element={<ShelvesPage />} />

                  <Route path="/admin/users" element={
                    <PageShell config={{ id: 'admin-users', title: 'Users Management', description: 'Manage user accounts', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <UsersPage />
                    </PageShell>
                  } />

                  <Route path="/admin/system-picks" element={
                    <PageShell config={{ id: 'admin-system-picks', title: 'System Picks', description: 'Manage system options', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <SystemPicksPage />
                    </PageShell>
                  } />

                  <Route path="/admin/template-mapping" element={
                    <PageShell config={{ id: 'admin-template-mapping', title: 'Template Mapping', description: 'Manage templates', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <TemplateMappingPage />
                    </PageShell>
                  } />

                  <Route path="/admin/system-health" element={<SystemHealthPage />} />
                  <Route path="/admin/label-designer" element={<LabelDesignerPage />} />
                  <Route path="/admin/returns" element={<AdminReturnsPage />} />

                  <Route path="/admin/ai-monitoring" element={
                    <PageShell config={{ id: 'admin-ai-monitoring', title: 'AI Monitoring Dashboard', description: 'Monitor AI processing jobs', actions: [], layout: { containerWidth: 'xl', columns: 1 } }}>
                      <AIMonitoringPage />
                    </PageShell>
                  } />

                  <Route path="/admin/tiers" element={<TiersPage />} />
                  <Route path="/tiers/:id" element={<TierDetailPage />} />

                  <Route path="/escaperoute" element={<EscapeRoutePage />} />
                </Routes>
              </Router>
            </NotificationProvider>
          </MobileProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
